/* eslint-disable no-debugger */
import React, {
  FunctionComponent,
  useState,
  useEffect,
  ChangeEvent,
  useContext,
} from "react";
import { Button, ChoiceList, TextField } from "@cmsgov/ds-medicare-gov";
import { County } from "../@types";
import * as api from "../api";
import { useTranslate } from "../helpers";
import { limitInputLength } from "../helpers/objectUtilities";
import states from "../helpers/states";
import { isValidZipcode } from "../helpers/zipcodeHelpers";
import { AppContext } from "../app/store";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  TealiumEventProps,
  AnalyticsButtonType,
} from "../app/contexts/Analytics/types";
import { MandatoryDialog } from "../components";
interface StatePickerProps {
  label: string;
  onStateSelect: (county?: County, zipcode?: string) => void;
  initialZip?: string;
  initialFips?: string;
  className?: string;
  onInputTealiumProps?: TealiumEventProps;
  onBtnClickAnalytics?: () => void;
  pickerIndex?: number;
}

export const StatePicker: FunctionComponent<StatePickerProps> = ({
  label,
  onStateSelect,
  initialZip = "",
  initialFips,
  className,
  onInputTealiumProps,
  onBtnClickAnalytics,
  pickerIndex = 0,
}) => {
  const t = useTranslate();
  const { dispatch } = useContext(AppContext);
  const [fetching, setFetching] = useState(false);
  const [zipcode, setZipcode] = useState(initialZip);
  const [zipEntered, setZipEntered] = useState(!!initialZip);
  const [countyResults, setCountyResults] = useState<County[]>([]);
  const [selectedCounty, setSelectedCounty] = useState<County | null>(null);

  const [zipErrorMessage, setZipErrorMessage] = useState<undefined | string>(
    undefined
  );
  const [selectStateError, setSelectStateError] = useState<undefined | string>(
    undefined
  );

  const [showModal, setShowModal] = useState(false);
  const [autocompleteInput, setAutocompleteInput] =
    useState<HTMLInputElement | null>(null);

  const getSetOfStatesFromCounties = (countyList: County[]): Set<string> =>
    new Set(countyList.map(county => county.state));

  const searchForCounties = async (autofillZip?: string): Promise<void> => {
    if (!autocompleteInput) {
      return;
    }
    setZipErrorMessage(undefined);

    let input;
    if (autofillZip) {
      input = autofillZip;
      autocompleteInput.value = input;
    } else {
      input = autocompleteInput.value;
    }

    if (isValidZipcode(input)) {
      setZipcode(input);
      setFetching(true);
      setCountyResults([]);
      onStateSelect();
      if (!zipEntered) {
        // Not recording zip, and only sending once
        if (onInputTealiumProps) {
          dispatch({
            type: AnalyticsActionType.SEND_TEALIUM_EVENT,
            settings: onInputTealiumProps,
          });
        }
        setZipEntered(true);
      }
      try {
        const counties = await api.getCounties(input);
        setCountyResults(counties);

        if (autofillZip) {
          const countyMatch = counties.find(
            county => county.fips === initialFips
          );

          if (countyMatch) {
            onStateSelect(countyMatch, autofillZip);
          }
        }

        const setOfCounties = getSetOfStatesFromCounties(counties);

        if (setOfCounties.size === 1) {
          onStateSelect(counties[0], input);
        } else if (setOfCounties.size > 1) {
          setShowModal(true);
        }
      } catch (error) {
        setZipErrorMessage(t("state_picker.error_fetching_message"));
      }
      setFetching(false);
    } else {
      setZipErrorMessage(t("state_picker.error_message"));
    }
  };

  const onStateSelected = (e: React.ChangeEvent<HTMLInputElement>): void => {
    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_action: "Medigap - Find a Policy",
        event_label: "CTA: Location Selected",
      },
    });
    const selected = JSON.parse(e.target.value);

    setSelectedCounty(selected);

    if (!showModal) {
      onStateSelect(selected, zipcode);
    }
  };

  const onSubmit = (e: ChangeEvent<HTMLFormElement>): void => {
    e.preventDefault();
    searchForCounties();
  };

  useEffect(() => {
    if (initialZip && autocompleteInput) {
      searchForCounties(initialZip);
    }
  }, [initialZip, autocompleteInput]);

  return (
    <div className={`StatePicker ${className ? className : ""}`}>
      <form
        className="StatePicker__input e2e-select-county"
        onSubmit={onSubmit}
      >
        <TextField
          label={label}
          labelClassName="e2e-enter-zip StatePicker__label"
          className="ds-c-field--statePicker"
          onChange={(): void =>
            limitInputLength("statePicker input", 5, pickerIndex)
          }
          name="zip"
          inputRef={(ref: HTMLInputElement): void => {
            setAutocompleteInput(ref);
          }}
          errorMessage={zipErrorMessage}
        />

        <Button
          className="e2eStatePickerSelectBtn"
          type="submit"
          variation="solid"
          onClick={() => {
            dispatch({
              type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
              settings: {
                button: {
                  buttonStyle: AnalyticsButtonStyle.PRIMARY,
                  buttonType: AnalyticsButtonType.SUBMIT,
                  text: t("state_picker.button_text"),
                },
              },
            });
            if (onBtnClickAnalytics) {
              onBtnClickAnalytics();
            }
          }}
        >
          {t("state_picker.button_text")}
        </Button>
      </form>
      {fetching && <p>{t("app.loading")}...</p>}
      {showModal && (
        <MandatoryDialog
          heading={t("county_picker.choose_your_state")}
          className="SelectStateModal"
          headerClassName="SelectStateModal__header"
          onExit={(): void => setShowModal(false)}
          actions={[
            <Button
              className="ds-u-margin-right--2"
              key="primary"
              variation="solid"
              onClick={(): void => {
                if (selectedCounty) {
                  onStateSelect(selectedCounty, zipcode);
                  dispatch({
                    type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                    settings: {
                      event_action: "Medigap - Find a Policy",
                      event_label: "CTA: Continue",
                    },
                  });
                } else {
                  setSelectStateError(
                    t("state_picker.modal.error_message.state_not_selected")
                  );
                }
                dispatch({
                  type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                  settings: {
                    button: {
                      buttonStyle: AnalyticsButtonStyle.PRIMARY,
                      buttonType: AnalyticsButtonType.BUTTON,
                      text: t("state_picker.modal.button_text"),
                    },
                  },
                });
              }}
            >
              {t("state_picker.modal.button_text")}
            </Button>,
          ]}
        >
          <ChoiceList
            name="state"
            label={""}
            type="radio"
            errorMessage={selectStateError}
            choices={Array.from(getSetOfStatesFromCounties(countyResults)).map(
              state => ({
                label: states.find(s => s.abbreviation === state)?.name || "",
                value: JSON.stringify(
                  countyResults.find(county => county.state === state)
                ),
              })
            )}
            onChange={onStateSelected}
          />
        </MandatoryDialog>
      )}
    </div>
  );
};

export default StatePicker;
